'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
  type FormEvent,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { BEFORE_SCREEN_NAVIGATION, CLOSE_SCREEN_VIEW, type ScreenCloseDetail } from '@/lib/nav/screen-control';
import { Check, PanelRightClose, PanelRightOpen, X } from 'lucide-react';
import { Icon } from './Icon';
import { Button } from './Button';
import { holdReloads } from '@/lib/reload-guard';
import { cn } from './cn';
import { useBackdropDismiss } from './backdrop-dismiss';
import { useBottomSheet } from '@/hooks/useBottomSheet';
import { useUiGlass } from '@/lib/ui-variant/use-ui-glass';
import {
  pushSheet,
  popSheet,
  isTopmost,
  subscribeSheetStack,
  lockBodyScroll,
} from './sheet-stack';

/**
 * `<Sheet>` — the shared modal / bottom-sheet primitive.
 *
 * Absorbs the chrome every modal used to hand-roll: the `useBottomSheet`
 * wiring, the `isMobile ? ref : undefined` ternaries, the backdrop → sheet →
 * grabber scaffolding, and (new) Esc-to-close, background scroll-lock and a
 * focus-trap. Callers supply only the body via the slot sub-components:
 *
 *   <Sheet isMobile={isMobile} onClose={onClose}>
 *     <Sheet.Header title="…" meta={<StatusPill/>} actions={<EditPencil/>} />
 *     <Sheet.Form onSubmit={save}>
 *       <Sheet.Body withFooter form>…fields…</Sheet.Body>
 *       <Sheet.Footer>…buttons…</Sheet.Footer>
 *     </Sheet.Form>
 *   </Sheet>
 *
 * View / picker sheets skip <Sheet.Form> and render <Sheet.Body> directly.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isVisible(el: HTMLElement): boolean {
  return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

/** Handle passed to a function-child so consumer-defined buttons (Cancel,
 *  custom actions) can trigger the same animated close the X and backdrop use. */
export interface SheetApi {
  /** Animated dismiss on mobile, immediate on desktop — same as the X. */
  dismiss: () => void;
}

/**
 * What a <Sheet.Form> tells its <Sheet> so an ambient dismiss can ask before
 * throwing work away. Registered on mount, read lazily at the moment someone
 * tries to close — never during render.
 */
interface CloseGuard {
  /** Has the user actually changed anything since the sheet opened? */
  isDirty: () => boolean;
  /** Submit the form — the same path the footer's save button takes. */
  save: () => void;
  /** The form's own save button text, for the question we ask. */
  saveLabel: () => string;
}

interface SheetContextValue {
  /** Closes immediately, no questions. Cancel buttons mean discard, so they use this. */
  handleClose: () => void;
  /** Closes unless there are unsaved edits, in which case it asks first. */
  requestClose: () => void;
  dismissible: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  titleId: string;
  /** Sheet.Form uses this to decide whether a keyboard hint is worth showing. */
  isMobile: boolean;
  registerCloseGuard: (guard: CloseGuard | null) => void;
}

const SheetContext = createContext<SheetContextValue | null>(null);

function useSheetContext(): SheetContextValue {
  const ctx = useContext(SheetContext);
  if (!ctx) throw new Error('<Sheet.*> must be rendered inside <Sheet>');
  return ctx;
}

/**
 * Returns the sheet's animated dismiss (slide-down on mobile, immediate on
 * desktop) — the same close the X and backdrop use. For buttons living in a
 * descendant component (e.g. a separate ViewMode/EditMode) where the
 * function-child `{dismiss}` handle isn't in scope. Must be called inside <Sheet>.
 */
export function useSheetDismiss(): () => void {
  return useSheetContext().handleClose;
}

/**
 * The dismiss that ASKS first — the header X's: with unsaved edits in a
 * <Sheet.Form> it raises "Save before closing?" rather than closing. For a
 * sheet that draws its own close button instead of <Sheet.Header>'s (the glass
 * mail composer's floating X), so that button is not the one way out that
 * throws typed words away without a word.
 */
export function useSheetRequestClose(): () => void {
  return useSheetContext().requestClose;
}

/**
 * The id the dialog's `aria-labelledby` points at.
 *
 * `<Sheet.Header>` sets it on the title it renders, so a sheet using the header
 * is labelled for free. A sheet that builds its own hero instead — because the
 * thing it is offering deserves display type rather than a title bar — must put
 * this on whatever element is its heading, or the dialog announces with no name
 * at all and nothing anywhere says so.
 */
export function useSheetTitleId(): string {
  return useSheetContext().titleId;
}

interface SheetProps {
  onClose: () => void;
  /** Mobile renders the draggable bottom-sheet; desktop a centred dialog. */
  isMobile?: boolean;
  /** When false, backdrop-click and Esc do not close (e.g. mid-save). Default true. */
  dismissible?: boolean;
  /** Render into a portal at document.body — for sheets opened inside other
   *  sheets or inside clip-path'd containers (rationale sheets, RemoveMember). */
  portal?: boolean;
  /** Trigger element. When supplied, the mobile sheet grows out of / collapses
   *  back into this element (the "genie" effect) instead of sliding up, and the
   *  trigger pops on open + close. Desktop is unaffected. */
  originRef?: RefObject<HTMLElement | null>;
  className?: string;
  style?: CSSProperties;
  /**
   * Extra class on the BACKDROP, not the sheet.
   *
   * 🚨 Exists because the backdrop carries the z-index, and one surface sits
   * above the whole normal stack: the offline overlay (9500) covers everything
   * so an engineer with no signal can't tap through to a live app. A sheet
   * opened from there portals to `body` at the ordinary 230 and renders
   * INVISIBLY BEHIND IT — the button appears dead while the sheet is genuinely
   * open, which is what it did on a device.
   */
  backdropClassName?: string;
  /** ReactNode, or a function given a {dismiss} handle for consumer-defined
   *  close buttons (the animated mobile dismiss the X/backdrop also use). */
  children: ReactNode | ((api: SheetApi) => ReactNode);
}

export function Sheet({
  onClose,
  isMobile = false,
  dismissible = true,
  portal = false,
  originRef,
  className,
  style,
  backdropClassName,
  children,
}: SheetProps) {
  const reactId = useId();
  const titleId = `${reactId}-title`;
  const scrollRef = useRef<HTMLDivElement>(null);
  const sheetElRef = useRef<HTMLDivElement>(null);

  // Genie mode only applies on mobile (where the sheet/backdrop refs attach).
  const genie = isMobile && !!originRef;
  /**
   * Every sheet on a phone in Liquid Glass wears the same form furniture as the
   * quote builder: rounded fields, small-capital labels, switches for the tick
   * boxes, pill buttons (prototype v29). One marker here rather than a class
   * passed by each of the hundred-odd sheets, so a new sheet is glass without
   * anyone remembering to make it so.
   */
  const glass = useUiGlass() && isMobile;

  /**
   * The form's unsaved-work guard, if this sheet has a <Sheet.Form>. A ref, not
   * state: registering must not re-render, and it is only ever read inside an
   * event handler.
   *
   * Declared above useBottomSheet because the swipe-to-dismiss gesture consults
   * it before it will let go of the sheet.
   */
  const closeGuardRef = useRef<CloseGuard | null>(null);
  const registerCloseGuard = useCallback((guard: CloseGuard | null) => {
    closeGuardRef.current = guard;
  }, []);

  /** The form's save-button label while the unsaved-work question is up, else null. */
  const [confirmCloseLabel, setConfirmCloseLabel] = useState<string | null>(null);

  /**
   * May a swipe-down actually dismiss this sheet? Answered at the end of the
   * gesture; false springs the sheet back instead.
   *
   * The phone equivalent of the backdrop click, and it needs the same two
   * answers — a sheet held open mid-save must not be swipeable either, which it
   * was, `dismissible` having only ever gated the backdrop and Esc.
   */
  const allowSwipeDismiss = useCallback(() => {
    if (!dismissible) return false;
    const guard = closeGuardRef.current;
    if (guard && guard.isDirty()) {
      setConfirmCloseLabel(guard.saveLabel());
      return false;
    }
    return true;
  }, [dismissible]);

  const { sheetRef, backdropRef, sheetStyle, backdropStyle, handleClose: sheetClose } =
    useBottomSheet(onClose, scrollRef, genie ? originRef : undefined, allowSwipeDismiss);
  const handleClose = isMobile ? sheetClose : onClose;

  /**
   * The dismiss that asks first.
   *
   * Backdrop, Esc and the X are all *ambient* — nobody pressed a button that
   * says "throw this away" — so if the form has been touched since it opened,
   * they open a question instead of closing. A Cancel button is not ambient:
   * it means discard, and goes on using `handleClose` directly.
   */
  const requestClose = useCallback(() => {
    const guard = closeGuardRef.current;
    if (guard && guard.isDirty()) {
      setConfirmCloseLabel(guard.saveLabel());
      return;
    }
    handleClose();
  }, [handleClose]);

  /** Dismisses only on a press that begins AND ends on the backdrop — see the hook. */
  const backdropProps = useBackdropDismiss(
    useCallback(() => {
      if (dismissible) requestClose();
    }, [dismissible, requestClose]),
  );

  const [topmost, setTopmost] = useState(false);

  // Register in the stack so the topmost sheet owns Esc + focus-trap.
  useEffect(() => {
    pushSheet(reactId);
    const update = () => setTopmost(isTopmost(reactId));
    const unsub = subscribeSheetStack(update);
    update();
    return () => {
      unsub();
      popSheet(reactId);
    };
  }, [reactId]);

  // Voice dismissal uses the same guard as Escape and the header X. Before a
  // route change EVERY sheet may veto, including an edited form under a preview.
  useEffect(() => {
    const beforeNavigate = (event: Event) => {
      if (event.defaultPrevented) return;
      if (!dismissible || closeGuardRef.current?.isDirty()) {
        event.preventDefault();
        if (dismissible) requestClose();
      }
    };
    const closeView = (event: Event) => {
      if (!isTopmost(reactId)) return;
      const detail = (event as CustomEvent<ScreenCloseDetail>).detail;
      detail.outcome = !dismissible ? 'busy' : closeGuardRef.current?.isDirty() ? 'unsaved' : 'closing';
      if (dismissible) requestClose();
    };
    window.addEventListener(BEFORE_SCREEN_NAVIGATION, beforeNavigate);
    window.addEventListener(CLOSE_SCREEN_VIEW, closeView);
    return () => {
      window.removeEventListener(BEFORE_SCREEN_NAVIGATION, beforeNavigate);
      window.removeEventListener(CLOSE_SCREEN_VIEW, closeView);
    };
  }, [dismissible, reactId, requestClose]);

  // Background scroll-lock (ref-counted across nested sheets).
  useEffect(() => lockBodyScroll(), []);

  // Remember what opened the sheet, pull focus in (unless an autoFocus'd
  // field already took it), and restore focus to the trigger on close.
  useEffect(() => {
    const trigger = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      const sheet = sheetElRef.current;
      // Land focus on the dialog CONTAINER, not the first focusable element —
      // focusing e.g. the header edit pencil flashes a programmatic (blue)
      // focus ring on open. An autoFocus'd field keeps its focus via the
      // contains() guard. Keyboard Tab still moves to the real controls.
      if (sheet && !sheet.contains(document.activeElement)) {
        sheet.focus();
      }
    }, 0);
    return () => {
      window.clearTimeout(t);
      if (trigger && document.contains(trigger)) {
        try {
          trigger.focus();
        } catch {
          /* element no longer focusable — ignore */
        }
      }
    };
  }, []);

  // Esc-to-close + Tab focus-trap — only while this is the topmost sheet.
  useEffect(() => {
    if (!topmost) return;
    function onKeyDown(e: KeyboardEvent) {
      const sheet = sheetElRef.current;
      if (!sheet) return;
      if (e.key === 'Escape') {
        if (dismissible) {
          e.preventDefault();
          requestClose();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = Array.from(sheet.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);
      if (nodes.length === 0) {
        e.preventDefault();
        sheet.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !sheet.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [topmost, dismissible, requestClose]);

  const ctx: SheetContextValue = {
    handleClose,
    requestClose,
    dismissible,
    scrollRef,
    titleId,
    isMobile,
    registerCloseGuard,
  };

  // Merge our ref-callback with the hook's sheetRef so we always have the
  // sheet element for focus management, even on desktop where the hook ref
  // isn't applied. Stable across renders so React doesn't detach/reattach it.
  const setSheetEl = useCallback(
    (el: HTMLDivElement | null) => {
      sheetElRef.current = el;
      if (isMobile) sheetRef.current = el;
    },
    [isMobile, sheetRef],
  );

  const tree = (
    <>
    <div
      className={cn('mucka-mod-backdrop', backdropClassName)}
      ref={isMobile ? backdropRef : undefined}
      style={isMobile ? backdropStyle : undefined}
      {...backdropProps}
    >
      <div
        className={cn('mucka-mod-sheet', genie && 'mucka-mod-sheet--genie', glass && 'mucka-mod-sheet--glass', className)}
        ref={setSheetEl}
        style={{ outline: 'none', ...(isMobile ? sheetStyle : null), ...style }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        {isMobile && <div className="mucka-mod-grabber" aria-hidden />}
        <SheetContext.Provider value={ctx}>
          {typeof children === 'function' ? children({ dismiss: handleClose }) : children}
        </SheetContext.Provider>
      </div>
    </div>
    {confirmCloseLabel !== null && (
      <SaveCheck
        // The only one of the two questions that reaches a phone — the Enter
        // question is desktop-only — so it is also the only one that needs to
        // arrive as a proper bottom sheet rather than a centred dialog.
        isMobile={isMobile}
        title="Save before closing?"
        body="You've changed something here and it isn't saved yet. Save it now, or close and lose the changes."
        saveLabel={confirmCloseLabel}
        discardLabel="Close without saving"
        onCancel={() => setConfirmCloseLabel(null)}
        onDiscard={() => {
          setConfirmCloseLabel(null);
          handleClose();
        }}
        onConfirm={() => {
          const guard = closeGuardRef.current;
          setConfirmCloseLabel(null);
          guard?.save();
        }}
      />
    )}
    </>
  );

  if (portal && typeof document !== 'undefined') {
    return createPortal(tree, document.body);
  }
  return tree;
}

// ── Header ───────────────────────────────────────────────────────────────

interface SheetHeaderProps {
  title: ReactNode;
  /** 'default' (no avatar), 'avatar' (56px initials box), 'list' (title + subtitle). */
  variant?: 'default' | 'avatar' | 'list';
  /** Meta column to the right of the name — status pills / role chips. */
  meta?: ReactNode;
  /** Subtitle line for the 'list' variant (e.g. "3 on site · 2 travelling"). */
  subtitle?: ReactNode;
  /** Avatar box content for the 'avatar' variant (e.g. initials). */
  avatar?: ReactNode;
  /** Extra header buttons placed before the close X (e.g. an edit pencil). */
  actions?: ReactNode;
  /** Show the close X. Default true. */
  showClose?: boolean;
}

function SheetHeader({
  title,
  variant = 'default',
  meta,
  subtitle,
  avatar,
  actions,
  showClose = true,
}: SheetHeaderProps) {
  const { requestClose, dismissible, titleId } = useSheetContext();

  const close = showClose ? (
    <button
      type="button"
      className="mucka-mod-close"
      // The X asks about unsaved work too — it sits a few pixels from the edit
      // pencil and gets caught by a hand aiming for something else.
      onClick={requestClose}
      disabled={!dismissible}
      aria-label="Close"
    >
      <Icon icon={X} size={20} strokeWidth={2.5} />
    </button>
  ) : null;

  if (variant === 'list') {
    return (
      <header className="mucka-mod-head mucka-mod-head--list">
        <div className="mucka-mod-head-listtext">
          <div className="mucka-mod-head-name" id={titleId}>
            {title}
          </div>
          {subtitle != null && <div className="mucka-mod-head-sub">{subtitle}</div>}
        </div>
        {actions}
        {close}
      </header>
    );
  }

  const isAvatar = variant === 'avatar';
  return (
    <header className={cn('mucka-mod-head', !isAvatar && 'mucka-mod-head--noavatar')}>
      {isAvatar && (
        <div className="mucka-mod-head-avatar" aria-hidden>
          {avatar}
        </div>
      )}
      <div className="mucka-mod-head-text">
        <div className="mucka-mod-head-name" id={titleId}>
          {title}
        </div>
        {meta != null && <div className="mucka-mod-head-meta">{meta}</div>}
      </div>
      {actions}
      {close}
    </header>
  );
}

// ── Form (optional wrapper around Body + Footer) ──────────────────────────

interface SheetFormProps {
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  className?: string;
  /**
   * Override the automatic unsaved-work detection.
   *
   * By default the form watches for native `input` / `change` events bubbling
   * out of its own fields, which is "the user physically changed something" and
   * covers every ordinary field for free. Pass this when a sheet's edits happen
   * somewhere that never fires one — a canvas signature, a drag-reorder, a
   * picker that only sets React state — or `false` to opt out of the question
   * entirely.
   */
  dirty?: boolean;
  children: ReactNode;
}

/**
 * The form's own save label, read from the DOM at the moment we ask.
 *
 * Deliberately not a prop. Every sheet already renders its submit button
 * with the right words on it — "Create job", "Save changes", "Send quote" —
 * and reading them means all ~46 forms get an accurate dialog without being
 * touched, and a form that later renames its button can't drift out of sync
 * with what the dialog claims it does.
 */
function saveLabel(form: HTMLFormElement | null): string {
  const btn = form?.querySelector<HTMLButtonElement>('button[type="submit"]');
  const text = btn?.textContent?.trim();
  return text && text.length > 0 ? text : 'Save';
}

/** Mac gets the glyph, everyone else the word. */
function saveChordLabel(): string {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
  return mac ? '⌘S' : 'Ctrl+S';
}

/**
 * The little dialog that stands between a half-finished form and losing it.
 *
 * Two questions use it. "You pressed Enter — did you mean to save and close?"
 * (from <Sheet.Form>), and "you're closing a form you've changed — save first?"
 * (from <Sheet>, on a backdrop click, Esc or the X). The second adds a third
 * button, because there the do-nothing answer and the throw-it-away answer are
 * genuinely different things.
 *
 * 🚨 Cancel holds the focus, NOT the save.
 *
 * The press that opens this dialog is usually the ACCIDENTAL one — Enter at
 * the end of a line, out of habit. If the save button were focused, a second
 * reflex Enter would save and close exactly as before, and the dialog would
 * have bought one keystroke of delay and nothing else. Focused on Cancel, a
 * reflex Enter returns you to the form with everything intact, and saving
 * stays a thing you have to look at and choose.
 *
 * Built on <Sheet portal> and the <Button> primitives rather than hand-rolled,
 * so it is the same chrome as every other decision sheet in the app — the
 * chamfered silhouette, Söhne, and the dovetail buttons. A first cut portalled
 * its own div to document.body with `.mucka-btn` classes written by hand: the
 * parent sheet's focus trap fought it, and the buttons came out as plain
 * rounded rectangles that looked nothing like Mucka.
 *
 * No <Sheet.Form> here on purpose. It would nest a second form and bring its
 * own Enter handling to a dialog that exists to answer an Enter.
 */
function SaveCheck({
  isMobile,
  title,
  body,
  hint,
  saveLabel,
  discardLabel,
  onCancel,
  onDiscard,
  onConfirm,
}: {
  isMobile?: boolean;
  title: string;
  body: ReactNode;
  /** The keyboard footnote — only the Enter question has one. */
  hint?: ReactNode;
  saveLabel: string;
  /** When set, a third way out: close and lose it. Omitted by the Enter question,
   *  where "Keep editing" already IS the do-nothing answer. */
  discardLabel?: string;
  onCancel: () => void;
  onDiscard?: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  /**
   * 🚨 `autoFocus` alone loses a race. <Sheet> pulls focus to its own container
   * on mount via a setTimeout(0), and whether that lands before or after
   * React's autoFocus is not deterministic — it flipped between test runs, so
   * it would flip on a real machine too, and the dialog would sometimes open
   * with nothing focused but the box.
   *
   * Child effects run before parent effects, so the Sheet's timer is always
   * scheduled first and this one always fires after it.
   */
  useEffect(() => {
    const t = window.setTimeout(() => cancelRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <Sheet
      portal
      isMobile={isMobile}
      onClose={onCancel}
      className={cn('mucka-savecheck', discardLabel && 'mucka-savecheck--wide')}
      backdropClassName="mucka-savecheck-backdrop"
    >
      <Sheet.Header title={title} />
      <Sheet.Body withFooter>
        <p className="mucka-savecheck-body t-body-md">{body}</p>
        {hint}
      </Sheet.Body>
      <Sheet.Footer className="mucka-savecheck-actions">
        {/* Discard sits apart from the other two — it is the one that loses work,
            so it never lands under a hand aiming for Keep editing. */}
        {discardLabel && onDiscard && (
          <Button
            variant="ghost"
            size="md"
            trailingIcon={null}
            className="mucka-savecheck-discard"
            onClick={onDiscard}
          >
            {discardLabel}
          </Button>
        )}
        {/* autoFocus AND the effect above — see the note on the race. */}
        <Button
          ref={cancelRef}
          autoFocus
          variant="secondary"
          size="md"
          trailingIcon={null}
          onClick={onCancel}
        >
          Keep editing
        </Button>
        <Button
          variant="primary"
          size="md"
          leadingIcon={Check}
          className="mucka-mod-savebtn"
          onClick={onConfirm}
        >
          {saveLabel}
        </Button>
      </Sheet.Footer>
    </Sheet>
  );
}

function SheetForm({ onSubmit, className, dirty, children }: SheetFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const { isMobile, registerCloseGuard } = useSheetContext();
  /**
   * Enter was pressed — the save button's label, captured at that moment, or
   * null when nothing is being asked. Holding the LABEL rather than a boolean
   * keeps the DOM read in the event handler where it belongs; reading the form
   * ref while rendering the dialog is a render-phase ref access.
   */
  const [confirmLabel, setConfirmLabel] = useState<string | null>(null);

  /**
   * Has anyone touched this form? Set from native `input` / `change` events
   * bubbling out of the fields, which means a *person* changed a control.
   *
   * 🚨 Deliberately not a snapshot-and-compare of the field values. Half these
   * sheets fill themselves in after they open — a property lookup landing, a
   * price list resolving, defaults arriving from the account — and against a
   * snapshot taken on mount every one of those reads as an edit, so the sheet
   * would demand an answer from someone who had done nothing but open it. A
   * React state update fires no native event; a keystroke does.
   */
  const dirtyRef = useRef(false);
  /** Read inside the guard's closure, so the guard itself registers once. */
  const dirtyPropRef = useRef(dirty);
  useEffect(() => {
    dirtyPropRef.current = dirty;
  }, [dirty]);

  /**
   * 🚨 A FORM WITH EDITS IN IT HOLDS RELOADS OFF.
   *
   * The app applies a pending update by reloading the page, at moments chosen
   * to be safe — arriving at home, coming back after fifteen minutes away
   * (`src/lib/update-apply.ts`). "Safe" there means "the page was being torn
   * down anyway", which is true of the route and false of an open sheet: a
   * desktop tab switched to email for fifteen minutes comes back to a reload,
   * and a half-priced quote is gone. None of these sheets persist their state.
   *
   * Rather than teach every rule about every modal, the form says "not right
   * now" through the same guard a live voice call uses. That makes every rule
   * safe by construction, forced updates included, and it is the only reason
   * the four-hour backstop is defensible.
   *
   * Taken on the FIRST edit, not on mount — an untouched sheet has nothing to
   * lose and must not block the app updating for as long as it happens to be
   * open. Released on unmount, which covers saving, cancelling and dismissing
   * alike.
   */
  const reloadHoldRef = useRef<(() => void) | null>(null);
  const holdForEdits = useCallback(() => {
    if (reloadHoldRef.current) return;
    reloadHoldRef.current = holdReloads();
  }, []);
  useEffect(() => () => {
    reloadHoldRef.current?.();
    reloadHoldRef.current = null;
  }, []);
  // The explicit `dirty` prop, for sheets whose edits fire no native event —
  // a signature canvas, a drag-reorder, a picker that only sets React state.
  useEffect(() => {
    if (dirty) holdForEdits();
  }, [dirty, holdForEdits]);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const mark = () => {
      dirtyRef.current = true;
      holdForEdits();
    };
    // Native listeners, not React's onChange: `input` and `change` both bubble
    // in the DOM, so one pair on the form catches every field beneath it —
    // including ones rendered by components that never see a React prop of ours.
    form.addEventListener('input', mark);
    form.addEventListener('change', mark);
    return () => {
      form.removeEventListener('input', mark);
      form.removeEventListener('change', mark);
    };
  }, [holdForEdits]);

  // Tell the enclosing <Sheet> how to ask before it throws this form away.
  useEffect(() => {
    registerCloseGuard({
      isDirty: () => (dirtyPropRef.current !== undefined ? dirtyPropRef.current : dirtyRef.current),
      save: () => formRef.current?.requestSubmit(),
      saveLabel: () => saveLabel(formRef.current),
    });
    return () => registerCloseGuard(null);
  }, [registerCloseGuard]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    /**
     * Cmd/Ctrl+S — the deliberate save, scoped to the form rather than the
     * document so it can't shadow the browser's Save Page anywhere else.
     * `requestSubmit` (not `submit`) so validation and onSubmit run, exactly
     * as clicking the footer button does.
     */
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
      e.preventDefault();
      formRef.current?.requestSubmit();
      return;
    }

    if (e.key !== 'Enter') return;

    /**
     * 🚨 Enter does NOT submit a sheet form.
     *
     * A form with a submit button implicitly submits on Enter in any text
     * input — and these forms save AND CLOSE. So the reflex Enter at the end
     * of a line, a quantity or a phone number closed the sheet on a
     * half-finished record. Reported on quotes by one customer and hit
     * again on the job modal.
     *
     * Deliberately NOT a confirm dialog: the dialog would fire mostly on the
     * accidental press, and its own primary action answers to Enter — so
     * Enter, Enter would save and close anyway, one keystroke later.
     *
     * Enter still does its normal thing everywhere it already meant something:
     * a newline in a textarea, activating a focused button (including Save),
     * opening a select. Only single-line inputs are neutralised.
     */
    /**
     * 🚨 Named as "what Enter legitimately does something in", NOT "what it
     * doesn't". The first cut allow-listed `tagName === 'INPUT'` and a native
     * `<select>` walked straight past it — Enter on a focused select is also
     * an implicit submit, so the job modal still saved and closed on a Tab
     * through Job Type. Any control not on this list gets Enter neutralised,
     * so the next widget added can't reopen the hole.
     */
    const el = e.target as HTMLElement | null;
    if (!el) return;
    const tag = el.tagName;
    // Newline, activate, follow — Enter is already theirs.
    if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A') return;
    if (tag === 'INPUT') {
      const type = (el as HTMLInputElement).type;
      if (type === 'submit' || type === 'button' || type === 'reset'
        || type === 'checkbox' || type === 'radio') return;
    }

    // A child already claimed this Enter — the price-list typeahead taking a
    // match, the postcode field running a lookup. Not a stray press, and not
    // a moment to ask anyone whether they meant to save.
    if (e.defaultPrevented) return;

    e.preventDefault();

    /**
     * Ask, rather than save. Enter never submits — it opens a confirmation
     * naming the form's own save button, so what's about to happen is on
     * screen before anything happens.
     *
     * Nothing to ask about on a touchscreen: the on-screen keyboard's Go key
     * isn't someone reaching to save, and a dialog there is just an obstacle.
     */
    if (!isMobile) setConfirmLabel(saveLabel(formRef.current));
  }

  return (
    <form
      ref={formRef}
      className={cn('mucka-mod-formwrap', className)}
      onSubmit={onSubmit}
      onKeyDown={handleKeyDown}
    >
      {children}
      {confirmLabel !== null && (
        <SaveCheck
          title="Save and close?"
          body={
            <>
              Nothing&apos;s saved yet. Save now and this closes — or carry on where you
              left off.
            </>
          }
          hint={
            <p className="mucka-savecheck-hint">
              <kbd className="mucka-savecheck-kbd">{saveChordLabel()}</kbd>
              saves any time, without asking.
            </p>
          }
          saveLabel={confirmLabel}
          onCancel={() => setConfirmLabel(null)}
          onConfirm={() => {
            setConfirmLabel(null);
            formRef.current?.requestSubmit();
          }}
        />
      )}
    </form>
  );
}

// ── Body (the scrollable region) ──────────────────────────────────────────

interface SheetBodyProps {
  /** Tightens the bottom padding because a sticky footer sits below. */
  withFooter?: boolean;
  /** Adds `mucka-mod-form` (tighter inter-field gap) for form bodies. */
  form?: boolean;
  className?: string;
  children: ReactNode;
}

function SheetBody({ withFooter, form, className, children }: SheetBodyProps) {
  const { scrollRef } = useSheetContext();
  return (
    <div
      ref={scrollRef}
      className={cn(
        'mucka-mod-body',
        form && 'mucka-mod-form',
        withFooter && 'mucka-mod-body--with-footer',
        className,
      )}
    >
      {children}
    </div>
  );
}

// ── Footer (sticky action bar) ────────────────────────────────────────────

interface SheetFooterProps {
  className?: string;
  children: ReactNode;
}

function SheetFooter({ className, children }: SheetFooterProps) {
  return <div className={cn('mucka-mod-formactions', className)}>{children}</div>;
}

// ── Desk (two-pane: form column + document page) ──────────────────────────

interface SheetDeskProps {
  /**
   * The document this form is writing — shown in the right-hand pane at
   * DESK_MQ and up, and not offered at all below it.
   *
   * Give it the LIVE draft, not the saved row. The whole point of the pane is
   * that the owner watches the customer's document being written; a pane that
   * lags behind the fields beside it is worse than no pane, because it looks
   * authoritative while being wrong.
   *
   * Safe to pass unconditionally: creating the element costs nothing while the
   * pane is shut, because React never renders what is not in the tree.
   */
  aside?: ReactNode;
  /** Quiet eyebrow above the page, e.g. "What Alistair sees". */
  asideLabel?: string;
  /** Controls for the preview, rendered in the eyebrow row beside its label. */
  asideActions?: ReactNode;
  /** Whether the pane is open. Closed shows the rail instead. */
  asideOpen?: boolean;
  /** Toggles the pane. Without it the rail is not offered at all. */
  onToggleAside?: () => void;
  children: ReactNode;
}

/**
 * Wraps a <Sheet.Body> so a second, non-scrolling-with-it pane can sit beside
 * it while the header and footer still span the full sheet.
 *
 * Below DESK_MQ the wrapper is `display: contents`, so the body stays a direct
 * flex child of the form exactly as it was before this existed — the narrow
 * and mobile layouts are untouched by construction rather than by testing.
 *
 * The pane opens from a rail down the right-hand edge and starts SHUT, so the
 * modal is the width it has always been until the owner asks for the document.
 * The caller owns that state — a sheet that reset it on every render would
 * shut the pane under someone mid-quote.
 */
function SheetDesk({ aside, asideLabel, asideActions, asideOpen, onToggleAside, children }: SheetDeskProps) {
  const offered = !!aside && !!onToggleAside;
  return (
    <div className={cn('mucka-mod-desk', offered && asideOpen && 'mucka-mod-desk--open')}>
      {children}
      {offered && !asideOpen && (
        <button
          type="button"
          className="mucka-mod-desk-rail"
          onClick={onToggleAside}
          aria-expanded={false}
          title={asideLabel || 'Preview'}
        >
          <Icon icon={PanelRightOpen} size={16} strokeWidth={2} />
          <span className="mucka-mod-desk-rail-label">Preview</span>
        </button>
      )}
      {offered && asideOpen && (
        <aside className="mucka-mod-desk-paper" aria-label={asideLabel || 'Document preview'}>
          <div className="mucka-mod-desk-eyebrowrow">
            <div className="mucka-mod-desk-eyebrow">{asideLabel}</div>
            {/* Controls that belong to the preview itself — the quote builder's
                page/PDF switch. In THIS row rather than under it: a bench that
                is already short of height should not spend a whole row on a
                two-button toggle. */}
            {asideActions}
            <button
              type="button"
              className="mucka-mod-desk-collapse"
              onClick={onToggleAside}
              aria-expanded
              aria-label="Hide the preview"
              title="Hide the preview"
            >
              <Icon icon={PanelRightClose} size={16} strokeWidth={2} />
            </button>
          </div>
          {aside}
        </aside>
      )}
    </div>
  );
}

Sheet.Header = SheetHeader;
Sheet.Form = SheetForm;
Sheet.Body = SheetBody;
Sheet.Footer = SheetFooter;
Sheet.Desk = SheetDesk;
